import { Box, Link } from "@mui/material";

interface SkipLinkProps {
  /** Target element ID (without #). Default: 'main-content' */
  targetId?: string;
  /** Link text. Default: 'Skip to main content' */
  children?: string;
}

/**
 * Skip link for keyboard users to bypass navigation.
 * Visually hidden until focused, then appears at top of page.
 *
 * @example
 * // In App.tsx or layout component
 * <SkipLink />
 * <AppHeader />
 * <main id="main-content">...</main>
 */
export function SkipLink({
  targetId = "main-content",
  children = "Skip to main content",
}: SkipLinkProps) {
  return (
    <Box
      component={Link}
      href={`#${targetId}`}
      sx={{
        // Visually hidden by default
        position: "absolute",
        left: "-9999px",
        zIndex: 9999,
        // Visible when focused
        "&:focus": {
          left: "50%",
          transform: "translateX(-50%)",
          top: 8,
          px: 3,
          py: 2,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          textDecoration: "none",
          borderRadius: 1,
          boxShadow: 3,
          fontWeight: 600,
          fontSize: "1rem",
          // High contrast focus ring
          outline: "2px solid",
          outlineColor: "primary.dark",
          outlineOffset: 2,
        },
      }}
    >
      {children}
    </Box>
  );
}

export default SkipLink;
